import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  BookOpen,
  Radar,
  HelpCircle,
  LogOut,
  MessageCircleQuestionMark,
  Mail,
  CalendarDays,
  Megaphone,
  Settings,
  SunMoon,
} from 'lucide-react';

import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ChangelogPopover } from './ChangelogPopover';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import CircularProgress from '@/Domain/components/Shared/CircularProgress/CircularProgress';
import { Dialog, DialogContent, DialogTitle } from '@/Domain/components/Shared/Dialog/Dialog';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLink,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { ThemeMenuItems } from '@/Domain/components/Shared/ThemeSelect/ThemeMenuItems';
import { TierPlanCard } from '@/Domain/components/TierPlanCard/TierPlanCard';
import { getUsageRingColor } from '@/Domain/components/TierPlanCard/usageColor';
import DiscordIcon from '@/Domain/components/icons/DiscordIcon';
import WhatsAppIcon from '@/Domain/components/icons/WhatsAppIcon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { UserHelper } from '@/Domain/helper/User.helper';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { useChangelog } from '@/Domain/hooks/Changelog/useChangelog.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';
import { useThemeStore } from '@/Domain/store/theme.store';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

const SUPPORT_EMAIL = 'support@quickchat.ai';

export function TopBarUserMenu() {
  const { t } = useTranslation('sidebar_menu');
  const { handleLogout } = useLogout();
  const { toggleOpen, setFeedbackPopoverOpen } = useSupportWidgetStore();
  const openCalModal = useCalModal();
  const postHog = usePostHog();
  const isDark = useThemeStore((state) => state.isDarkTheme());

  const { user } = useUser();
  const changelog = useChangelog();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: usage } = useQuery<Usage>({ queryKey: [QueryKey.subscription.usage] });
  const messagesUsed = usage?.messages_used ?? 0;
  const messagesLimit = usage?.messages_limit ?? 1;
  const percentUsed = messagesLimit > 0 ? Math.min(100, (messagesUsed / messagesLimit) * 100) : 0;
  // Purchased credits are still spendable, so never paint the agent as an outage.
  const severityPercent = (usage?.topup_credits ?? 0) > 0 ? Math.min(percentUsed, 99) : percentUsed;

  // Enterprise (CUSTOM) plans have no AI-credit limit, so there's nothing to ring.
  const { data: subscription } = useGetSubscription();
  const tier = subscription?.current_plan.tier;
  const showUsageRing = !!tier && tier !== TierEnum.CUSTOM;

  const openChangelog = () => {
    postHog?.capture(PostHogEventEnum.WhatsNewOpened);
    changelog.markAllSeen();
    setChangelogOpen(true);
  };

  const initials = user?.first_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U';
  const avatarInner = (
    <>
      <AvatarImage src={user?.avatar_url || undefined} />
      <AvatarFallback className="rounded-full text-xs">{initials}</AvatarFallback>
    </>
  );

  return (
    <>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          {/* Plain button, not IconButton: its `[&_svg]:size-4` rule would shrink the progress ring SVGs. */}
          <button
            type="button"
            className="group flex size-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25 cursor-pointer md:ml-1"
            aria-label={t('menu.account.menu_aria')}
            data-testid="topbar-user-menu-button"
          >
            {showUsageRing ? (
              <CircularProgress
                size={32}
                strokeWidth={2}
                maxCount={messagesLimit}
                currentCount={messagesUsed}
                color={getUsageRingColor(severityPercent, isDark)}
              >
                <Avatar className="size-6 rounded-full group-hover:hidden">{avatarInner}</Avatar>
                <span
                  className="hidden items-center justify-center text-[10px] font-semibold tabular-nums group-hover:flex"
                  style={{ color: getUsageRingColor(severityPercent, isDark) }}
                  data-testid="topbar-user-menu-usage-percent"
                >
                  {Math.round(percentUsed)}%
                </span>
              </CircularProgress>
            ) : (
              <Avatar className="size-7 rounded-full">{avatarInner}</Avatar>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-56" side="bottom" align="end" sideOffset={8}>
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-full">{avatarInner}</Avatar>
              <div className="flex flex-col flex-1 min-w-0 text-left text-sm leading-tight">
                <span className="truncate font-semibold text-popover-foreground">
                  {UserHelper.getFullName(user) || user?.email}
                </span>
                {UserHelper.getFullName(user) && (
                  <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showUsageRing && (
            <>
              {/* Clicking the embedded Upgrade CTA navigates; close the menu with it. */}
              <div className="-m-1 bg-muted p-1" onClick={() => setMenuOpen(false)}>
                <TierPlanCard open className="bg-card" source="top_bar" />
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLink to={IndexPath.settings}>
            <Settings />
            {t('menu.account.settings')}
          </DropdownMenuLink>
          <DropdownMenuItem onSelect={openChangelog} data-testid="topbar-user-menu-whats-new">
            <Bell />
            {t('menu.account.whats_new')}
            {changelog.hasUnseen && (
              <span
                className="ml-auto size-2 rounded-full bg-info"
                data-testid="topbar-user-menu-whats-new-dot"
              />
            )}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <HelpCircle />
              {t('menu.account.contact')}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={toggleOpen}>
                  <MessageCircleQuestionMark />
                  {t('menu.account.support_ai')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    window.open(
                      'https://wa.me/message/LHARULDFIUEZL1',
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <WhatsAppIcon />
                  {t('menu.account.whatsapp')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openCalModal(CAL_LINK.productDemo, 'topbar_menu')}>
                  <CalendarDays />
                  {t('menu.account.call')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    window.location.href = `mailto:${SUPPORT_EMAIL}`;
                    copyToClipboard(SUPPORT_EMAIL, () =>
                      toast.success(t('menu.account.email_copied')),
                    );
                  }}
                >
                  <Mail />
                  <span className="md:hidden">{t('menu.account.email_short')}</span>
                  <span className="hidden md:inline">
                    {t('menu.account.email', { email: SUPPORT_EMAIL })}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => setFeedbackPopoverOpen(true)}>
            <Megaphone />
            {t('menu.account.feedback')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              window.open('https://docs.quickchat.ai', '_blank', 'noopener,noreferrer')
            }
          >
            <BookOpen />
            {t('menu.account.documentation')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              window.open('https://status.quickchat.ai', '_blank', 'noopener,noreferrer')
            }
          >
            <Radar />
            {t('menu.account.status')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              window.open('https://discord.gg/KqkHwvPRNH', '_blank', 'noopener,noreferrer')
            }
          >
            <DiscordIcon />
            {t('menu.account.discord')}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoon />
              {t('menu.account.theme')}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <ThemeMenuItems />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleLogout()}>
            <LogOut />
            {t('menu.account.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={changelogOpen} onOpenChange={setChangelogOpen}>
        <DialogContent
          className="max-w-md gap-0 overflow-hidden p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">
            {t('changelog.popover_heading', { ns: 'topbar' })}
          </DialogTitle>
          <ChangelogPopover
            entries={changelog.entries}
            isLoading={changelog.isLoading}
            isError={changelog.isError}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
